import { fonts } from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { SITE_WIDTH, SITE_PADDING, SITE_PADDING_MOBILE, } from '@stories/Components/Containers/SiteWide/SiteWide.styles';
import styled, { css } from 'styled-components';
import { NavigationDropdownMode } from '../../Navigation.types';
import { ThemeSwitch } from '@helpers/theme';
const Container = styled.div `
  position: absolute;
  top: 100%;
  left: 0;
  display: block;
  background: #fff;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
  height: 490px;
  width: 100%;
  z-index: 10000;

  ${({ isActive }) => isActive
    ? css `
          visibility: visible;
          opacity: 1;
          pointer-events: auto;
        `
    : css `
          visibility: hidden;
          opacity: 0;
          pointer-events: none;
        `}

  ${({ mode }) => mode === NavigationDropdownMode.TwoLevels &&
    css `
      ${Column1} {
        flex-basis: ${(1 / 2) * 100}%;
      }

      ${Column2} {
        flex-basis: ${(1 / 2) * 100}%;

        ${List} {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
        }

        ${Item} {
          flex: 1 1 50%;
        }
      }

      ${Column3} {
        display: none;
      }

      @media ${from(Device.Desktop)} {
        ${Column1} {
          flex-basis: ${(1 / 4) * 100}%;
        }

        ${Column2} {
          flex-basis: ${(1 / 2) * 100}%;
        }

        ${Column3} {
          display: block;
          flex-basis: ${(1 / 4) * 100}%;
          margin-left: auto;
        }
      }

      @supports (display: grid) {
        ${Column1} {
          grid-column: 1 / span 4;
        }

        ${Column2} {
          grid-column: 5 / span 8;

          ${List} {
            display: grid;
            grid-template-rows: repeat(10, 1fr);
            grid-auto-flow: column;
          }

          ${Item} {
            grid-column: span 3;
          }
        }

        ${Column3} {
          display: none;
        }

        @media ${from(Device.Desktop)} {
          ${Column1} {
            grid-column: 1 / span 3;
          }

          ${Column2} {
            grid-column: 4 / span 6;
          }

          ${Column3} {
            display: block;
            grid-column: 10 / span 3;
            margin-left: 0;
          }
        }
      }
    `}

  ${({ mode }) => mode === NavigationDropdownMode.ThreeLevels &&
    css `
      ${Column1} {
        flex-basis: ${(1 / 3) * 100}%;
      }

      ${Column2} {
        flex-basis: ${(1 / 3) * 100}%;
      }

      ${Column3} {
        flex-basis: ${(1 / 3) * 100}%;
      }

      @supports (display: grid) {
        ${Column1} {
          grid-column: 1 / span 4;
        }

        ${Column2} {
          grid-column: 5 / span 4;
        }

        ${Column3} {
          grid-column: 9 / span 4;
          margin-left: -30px; // extend over gutter
        }
      }
    `}

  ${({ mode }) => mode === NavigationDropdownMode.ThreeLevelsPromo &&
    css `
      ${Column1} {
        flex-basis: ${(1 / 3) * 100}%;
      }

      ${Column2} {
        flex-basis: ${(1 / 3) * 100}%;
      }

      ${Column3} {
        flex-basis: ${(1 / 3) * 100}%;
      }

      ${Column4} {
        display: none;
      }

      @media ${from(Device.Desktop)} {
        ${Column1} {
          flex-basis: ${(1 / 4) * 100}%;
        }

        ${Column2} {
          flex-basis: ${(1 / 4) * 100}%;
        }

        ${Column3} {
          flex-basis: ${(1 / 4) * 100}%;
        }

        ${Column4} {
          display: block;
          flex-basis: ${(1 / 4) * 100}%;
          margin-left: auto;
        }
      }

      @supports (display: grid) {
        ${Column1} {
          grid-column: 1 / span 4;
        }

        ${Column2} {
          grid-column: 5 / span 4;
        }

        ${Column3} {
          grid-column: 9 / span 4;
          margin-left: -30px; // extend over gutter
        }

        ${Column4} {
          display: none;
        }

        @media ${from(Device.Desktop)} {
          ${Column1} {
            grid-column: 1 / span 3;
          }

          ${Column2} {
            grid-column: 4 / span 3;
          }

          ${Column3} {
            grid-column: 7 / span 3;
          }

          ${Column4} {
            display: block;
            grid-column: 10 / span 3;
            margin-left: 0;
          }
        }
      }
    `}
`;
const Inner = styled.div `
  position: relative;
  display: flex;
  flex-direction: row;
  height: 100%;
  margin: 0 auto;
  max-width: calc(${SITE_WIDTH}px + (${SITE_PADDING}px * 2));
  padding: 0 ${SITE_PADDING}px;
  width: 100%;

  @supports (display: grid) {
    display: grid;
    gap: 30px;
    grid-template-columns: repeat(12, 1fr);
  }

  @media ${until(Device.TabletLarge)} {
    display: flex;
    gap: 0; // resetting as only chrome supports this with flex.
    max-width: none;
    padding: 0 ${SITE_PADDING_MOBILE}px;
  }
`;
const Column = styled.div `
  display: block;
  flex: 0 1 auto;
  grid-row: 1;
  overflow-y: auto;
  transition: opacity 0.15s ease-out;

  /* ===== Scrollbar CSS ===== */
  /* Firefox */
  & {
    scrollbar-width: auto;
    ${ThemeSwitch({ groundforceColour: '#669900' })}
    scrollbar-color: var(--theme) #e5e5e5;
  }

  /* Chrome, Edge, and Safari */
  &::-webkit-scrollbar {
    width: 12px;
    margin-right: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #e5e5e5;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    ${ThemeSwitch({ groundforceColour: '#669900' })}
    background-color: var(--theme);
    border-radius: 10px;
    border: 3px solid #e5e5e5;
  }
`;
const Column1 = styled(Column) `
  @supports (display: grid) {
    margin-right: -30px; // extend over gutter
  }
`;
const Column2 = styled(Column) `
  display: none;
  background: #e5e5e5;

  ${({ isActive }) => isActive &&
    css `
      display: block;
    `}

  &::-webkit-scrollbar-track {
    background: #ffffff;
  }

  &::-webkit-scrollbar-thumb {
    border-color: #ffffff;
  }
`;
const Column3 = styled(Column) `
  display: none;

  ${({ isActive }) => isActive &&
    css `
      display: block;
    `}
`;
const Column4 = styled(Column) `
  display: none;

  ${({ isActive }) => isActive &&
    css `
      display: block;
    `}
`;
const List = styled.ul `
  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 0;
  padding: 20px 0;
`;
const Item = styled.li `
  display: block;
  list-style: none;
  margin: 0;
  padding: 0;
`;
const Link = styled.a `
  position: relative;
  display: block;
  background: linear-gradient(to bottom, #e5e5e5, #e5e5e5) no-repeat;
  background-size: 0% 100%;
  color: #000;
  ${fonts.DaxPro.Regular};
  font-size: 16px;
  font-weight: ${({ isBold }) => (isBold ? 700 : 400)};
  line-height: 20px;
  margin: 0;
  padding: 13px 20px 12px;
  text-decoration: none;
  transition: background-color 0.15s ease-out, background-size 0.15s ease-out, color 0.15s ease-out;
  ${ThemeSwitch({ groundforceColour: '#6f9d20' })}

  ${Column2} & {
    background-image: linear-gradient(to bottom, #f6f6f6, #f6f6f6);
    font-weight: 400;
  }

  ${Column3} & {
    padding-left: 50px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    background: linear-gradient(to bottom, var(--theme), var(--theme)) no-repeat;
    background-size: 100% 0%;
    height: 100%;
    transition: background-size 0.15s ease-out;
    width: 2px;
  }

  &:focus,
  &:hover {
    background-size: 100% 100%;
    color: #000;
    text-decoration: none;

    &::before {
      background-size: 100% 100%;
    }
  }

  ${({ isActive }) => isActive &&
    css `
      background-size: 100% 100%;
      color: #000;
      text-decoration: none;

      &::before {
        background-size: 100% 100%;
      }
    `}
`;
const YourSolutionColumn = styled(Column) `
  flex-basis: 70%;

  @supports (display: grid) {
    grid-column: 5 / -1;
  }
`;
export default {
    Column1,
    Column2,
    Column3,
    Column4,
    Container,
    Inner,
    Item,
    Link,
    List,
    YourSolutionColumn,
};
